import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SN_SUMMARY_COLUMNS } from "./card/sn-summary.columns";
import SnSummaryComponent from "./sn-summary.component";
import getCustomizeReportingSummary from "../../../../configs/apis/serialnumber-summary.js/get-customize-reporting-summary.api";
import exportCustomizeReportingSummary from "../../../../configs/apis/serialnumber-summary.js/export-customize-reporting-summary.api";
import { downloadExportedScanTrackSummary } from "../../utils/export-utils";
import { getCompanyList } from "../../../../configs/apis/serialnumber-summary.js/get-companyId.api";
import {
  fetchSnSummaryStart,
  fetchSnSummarySuccess,
  fetchSnSummaryFailure,
  setFilters,
  setVisibleColumns,
  setItemPerPage,
  setPage,
} from "../../redux/serial-number-summary/serial-number-summary.slice";

export default function SnSummaryContainer() {
  const [company, setCompany] = useState("");
  const [fromSequence, setFromSequence] = useState("");
  const [toSequence, setToSequence] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportingType, setExportingType] = useState("");
  const [exportError, setExportError] = useState(null);

  const {
    data,
    isLoading,
    filters,
    visibleColumns,
    itemPerPage,
    page,
    total,
    start,
    length,
    filteredCount,
    draw,
  } = useSelector((state) => state.snSummary);

  const dispatch = useDispatch();

  const columnView = Object.fromEntries(visibleColumns);

  const handleColumnChange = (column) => {
    const updated = visibleColumns.map(([key, value]) =>
      key === column ? [key, !value] : [key, value]
    );
    dispatch(setVisibleColumns(updated));
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    setExportingType(format);
    setExportError(null);
    try {
      // Map selected keys to display labels for backend
      const selectedLabels = Object.entries(columnView)
        .filter(([key, value]) => value)
        .map(([key]) => {
          const foundCol = SN_SUMMARY_COLUMNS.find((c) => c.key === key);
          return foundCol ? foundCol.label : key;
        });

      const params = {
        company_id: company,
        export_type: format,
        columns: selectedLabels, // Pass display labels to backend
      };
      const response = await exportCustomizeReportingSummary(params);
      const companyName = companyOptions.find(
        (opt) => opt.value === company
      )?.label;
      downloadExportedScanTrackSummary(response, format, companyName);
    } catch (error) {
      setExportError(error.message || "Export failed");
    } finally {
      setIsExporting(false);
      setExportingType("");
    }
  };

  const handlePageClick = (newPage) => {
    dispatch(setPage(newPage));
  };

  const handleItemPerPageChange = (newItemPerPage) => {
    dispatch(setItemPerPage(newItemPerPage));
    dispatch(setPage(0)); // Reset to first page
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const result = await getCompanyList();
        if (result.success && Array.isArray(result.data)) {
          const options = result.data.map((c) => ({
            value: c.id,
            label: c.name,
          }));
          setCompanyOptions(options);
          // Auto-select company if only one is available
          if (options.length === 1) {
            setCompany(options[0].value);
          }
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (!company) return;
    dispatch(fetchSnSummaryStart());
    const fetchData = async () => {
      try {
        const startVal = page * itemPerPage;
        const lengthVal = itemPerPage;
        const result = await getCustomizeReportingSummary({
          company_id: company,
          from_seq: fromSequence,
          to_seq: toSequence,
          start: startVal,
          length: lengthVal,
        });

        const keyMap = {};
        SN_SUMMARY_COLUMNS.forEach((col) => {
          keyMap[col.label] = col.key;
        });
        const mappedData = (result.data || []).map((row) => {
          const mappedRow = {};
          Object.entries(row).forEach(([k, v]) => {
            if (keyMap[k]) {
              mappedRow[keyMap[k]] = v;
            }
          });
          return mappedRow;
        });
        dispatch(
          fetchSnSummarySuccess({
            data: mappedData,
            total: result.SNTotalRecord || result.total || 0,
            start: result.start,
            length: result.length,
            filteredCount: result.filteredCount,
            draw: result.draw,
          })
        );
      } catch (error) {
        console.error("SN Summary API error:", error);
        dispatch(
          fetchSnSummaryFailure(error.message || "Failed to fetch data")
        );
      }
    };
    fetchData();
  }, [company, fromSequence, toSequence, page, itemPerPage, dispatch]);

  const getColumnLabel = (key) =>
    SN_SUMMARY_COLUMNS.find((col) => col.key === key)?.label || key;

  const checkedColumns = visibleColumns.filter(
    ([key, value]) => value && key && key.trim() !== ""
  );

  return (
    <SnSummaryComponent
      company={company}
      setCompany={setCompany}
      fromSequence={fromSequence}
      setFromSequence={setFromSequence}
      toSequence={toSequence}
      setToSequence={setToSequence}
      companyOptions={companyOptions}
      columnView={columnView}
      handleColumnChange={handleColumnChange}
      handleExport={handleExport}
      visibleColumns={checkedColumns}
      getColumnLabel={getColumnLabel}
      SN_SUMMARY_COLUMNS={SN_SUMMARY_COLUMNS}
      data={data}
      isLoading={isLoading}
      itemPerPage={itemPerPage}
      page={page}
      total={total}
      onPageClick={handlePageClick}
      onItemPerPageChange={handleItemPerPageChange}
      filteredCount={filteredCount}
      start={start}
      length={length}
      draw={draw}
      isExporting={isExporting}
      exportingType={exportingType}
      exportError={exportError}
    />
  );
}
